import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search as SearchIcon, MapPin, Phone, Globe, Loader2 } from 'lucide-react';
import { publicApi, resolveImage } from '../api';

export default function SearchResults() {
  const [params] = useSearchParams();
  const q = params.get('q') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterCity, setFilterCity] = useState('');
  const [filterCat, setFilterCat] = useState('');

  useEffect(() => {
    if (!q || q.length < 2) { setData(null); return; }
    setLoading(true);
    publicApi.search(q, {
      ...(filterCity && { city: filterCity }),
      ...(filterCat && { category: filterCat }),
    }).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [q, filterCity, filterCat]);

  return (
    <div className="max-w-6xl mx-auto px-6 py-16" data-testid="search-page">
      <div className="mb-8">
        <div className="flex items-center gap-3 text-white/90 mb-2">
          <SearchIcon size={22} />
          <h1 className="text-3xl md:text-4xl font-bold" style={{ color: 'var(--yup-primary)' }}>
            Risultati per "{q}"
          </h1>
        </div>
        {data && !loading && (
          <p className="text-sm text-white/70">{data.count} attività trovate</p>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <select
          value={filterCity}
          onChange={e => setFilterCity(e.target.value)}
          className="bg-black/40 border border-[color:var(--yup-border)] text-white text-sm rounded-md px-3 py-2"
          data-testid="search-filter-city"
        >
          <option value="">Tutte le città</option>
          <option value="desenzano">Desenzano</option>
          <option value="sirmione">Sirmione</option>
          <option value="peschiera">Peschiera</option>
        </select>
        <select
          value={filterCat}
          onChange={e => setFilterCat(e.target.value)}
          className="bg-black/40 border border-[color:var(--yup-border)] text-white text-sm rounded-md px-3 py-2"
          data-testid="search-filter-category"
        >
          <option value="">Tutte le categorie</option>
          {['ristoranti','divertimento','sport','cultura','natura','relax','emergenze',
            'trasporti','noleggi','supermercati','bancomat','partner','tour'].map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {loading && (
        <div className="flex justify-center py-16">
          <Loader2 size={32} className="animate-spin text-white/50" />
        </div>
      )}

      {!loading && data && data.count === 0 && (
        <div className="text-center py-16 text-white/60">
          Nessun risultato trovato per "{q}". Prova con un altro termine.
        </div>
      )}

      {!loading && data && data.results.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="search-results-grid">
          {data.results.map(a => (
            <div key={a.id} className="bg-[color:var(--yup-card)] border border-[color:var(--yup-border)] rounded-lg overflow-hidden hover:border-[color:var(--yup-primary)] transition"
                 data-testid={`search-result-${a.slug || a.id}`}>
              {a.image && (
                <img src={resolveImage(a.image)} alt={a.name} className="w-full h-40 object-cover" />
              )}
              <div className="p-4">
                <div className="text-xs uppercase tracking-widest text-[color:var(--yup-primary)] mb-1">
                  {a.category_key}{a.subcategory ? ` · ${a.subcategory}` : ''}
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{a.name}</h3>
                {a.address && (
                  <div className="flex items-start gap-2 text-sm text-white/70 mb-1">
                    <MapPin size={14} className="mt-0.5 flex-shrink-0" />
                    <span>{a.address}</span>
                  </div>
                )}
                {a.phone && (
                  <a href={`tel:${a.phone}`} className="flex items-center gap-2 text-sm text-white/70 hover:text-[color:var(--yup-primary)] mb-1">
                    <Phone size={14} /> {a.phone}
                  </a>
                )}
                {a.website && (
                  <a href={a.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-white/70 hover:text-[color:var(--yup-primary)]">
                    <Globe size={14} /> Sito ufficiale
                  </a>
                )}
                <div className="mt-3">
                  <Link to={`/${a.city_slug}/cosa-vedere/${a.category_key}${a.subcategory ? `/${a.subcategory}` : ''}`}
                        className="text-xs text-[color:var(--yup-primary)] hover:underline">
                    Vai alla categoria →
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
